import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, atualizarJson, enviarJson, pedirJson } from "./api";
import { ROTULO_DO_ESTADO } from "./tickets";
import type { EstadoDoTicket } from "./tickets";

/* Painéis configuráveis por perfil: o contrato da API (`api/app/dominios/paineis/`), os
   números legíveis e as contas puras do editor (ordem e largura na grade).

   Quem monta é `painel:gerenciar`. Quem vê abre o painel que o papel recebe, e o servidor só
   devolve os widgets que a pessoa pode ver — a tela não filtra nada por conta própria. */

export const CHAVE_DOS_PAINEIS = ["paineis"] as const;

const uuid = z.string().uuid();
const FILTROS_POSSIVEIS = ["periodo", "organizacao", "equipe"] as const;
export type FiltroDoWidget = (typeof FILTROS_POSSIVEIS)[number];
const esquemaFiltrosAceitos = z.array(z.enum(FILTROS_POSSIVEIS));

/** Os períodos que um widget aceita — os mesmos de `PERIODOS` na API. */
export const PERIODOS = [
  ["ultimos_7", "Últimos 7 dias"],
  ["ultimos_30", "Últimos 30 dias"],
  ["ultimos_90", "Últimos 90 dias"],
  ["mes_atual", "Mês atual"],
] as const;

export const COLUNAS_DA_GRADE = 4;
export const LARGURAS = [1, 2, 3, 4] as const;

/* Os filtros que um widget guarda: o que o servidor aceita e devolve. Chaves ausentes = sem
   restrição (ou o padrão do widget, no caso do período). */
const esquemaFiltrosGuardados = z.object({
  periodo: z.string().optional(),
  organizacao_id: z.string().optional(),
  equipe_id: z.string().optional(),
});
export type FiltrosGuardados = z.infer<typeof esquemaFiltrosGuardados>;

const UNIDADES = [
  "chamados",
  "percentual",
  "segundos",
  "minutos",
  "nota",
  "pontos",
  "registros",
] as const;
export type Unidade = (typeof UNIDADES)[number];

const esquemaNumero = z.object({
  chave: z.string(),
  rotulo: z.string(),
  valor: z.number().nullable(),
  unidade: z.enum(UNIDADES),
  detalhe: z.string().nullish(),
});

const esquemaDadosDoWidget = z.object({
  tipo: z.string(),
  titulo: z.string(),
  inicio: z.string().nullable(),
  fim: z.string().nullable(),
  numeros: z.array(esquemaNumero),
  aviso: z.string().nullish(),
});

const esquemaItemDoDrill = z.object({
  id: z.string(),
  chamado_id: uuid.nullable(),
  numero: z.number().int().nullable(),
  titulo: z.string(),
  organizacao: z.string().nullable(),
  estado: z.string().nullable(),
  detalhe: z.string().nullable(),
  em: z.string().nullable(),
});

const esquemaDrill = z.object({
  tipo: z.string(),
  numero: z.string(),
  rotulo: z.string(),
  total: z.number().int(),
  itens: z.array(esquemaItemDoDrill),
  nota: z.string().nullish(),
});

const esquemaDefinicao = z.object({
  tipo: z.string(),
  titulo: z.string(),
  descricao: z.string(),
  permissoes: z.array(z.string()),
  filtros: esquemaFiltrosAceitos,
  periodo_padrao: z.string(),
});

const esquemaCatalogo = z.object({
  widgets: z.array(esquemaDefinicao),
  periodos: z.array(z.object({ chave: z.string(), rotulo: z.string() })),
  papeis: z.array(
    z.object({ chave: z.string(), nome: z.string(), painel_atual: z.string().nullable() }),
  ),
});

const esquemaWidgetNoConsole = z.object({
  id: uuid,
  tipo: z.string(),
  colunas: z.number().int(),
  filtros: esquemaFiltrosGuardados,
});

const esquemaPainelNoConsole = z.object({
  id: uuid,
  nome: z.string(),
  descricao: z.string(),
  widgets: z.array(esquemaWidgetNoConsole),
  papeis: z.array(z.string()),
  criado_em: z.string(),
  atualizado_em: z.string(),
});

const esquemaWidgetParaVer = z.object({
  id: uuid,
  tipo: z.string(),
  titulo: z.string(),
  colunas: z.number().int(),
  filtros: esquemaFiltrosGuardados,
  filtros_aceitos: esquemaFiltrosAceitos,
  periodo_padrao: z.string(),
});

const esquemaPainelParaVer = z.object({
  id: uuid,
  nome: z.string(),
  descricao: z.string(),
  widgets: z.array(esquemaWidgetParaVer),
});

const esquemaPainelInicial = z.object({ painel: esquemaPainelParaVer.nullable() });

export type NumeroDoWidget = z.infer<typeof esquemaNumero>;
export type DadosDoWidget = z.infer<typeof esquemaDadosDoWidget>;
export type ItemDoDrill = z.infer<typeof esquemaItemDoDrill>;
export type DrillDoWidget = z.infer<typeof esquemaDrill>;
export type DefinicaoDeWidget = z.infer<typeof esquemaDefinicao>;
export type CatalogoDosPaineis = z.infer<typeof esquemaCatalogo>;
export type PainelNoConsole = z.infer<typeof esquemaPainelNoConsole>;
export type WidgetParaVer = z.infer<typeof esquemaWidgetParaVer>;
export type PainelParaVer = z.infer<typeof esquemaPainelParaVer>;

/* ---------------------------------------------------------------------------- helpers puros */

const inteiro = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
const umaCasa = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** "2 h 05 min", "45 min" ou "30 s": o tempo como a equipe fala, não em segundos. */
export function duracaoLegivel(segundos: number): string {
  if (segundos < 60) return `${Math.round(segundos)} s`;
  const minutos = Math.round(segundos / 60);
  return minutos >= 60
    ? `${Math.floor(minutos / 60)} h ${String(minutos % 60).padStart(2, "0")} min`
    : `${minutos} min`;
}

/** O valor de um número do widget com a unidade dele; sem dado é traço, nunca zero. */
export function valorLegivel(numero: Pick<NumeroDoWidget, "valor" | "unidade">): string {
  const { valor, unidade } = numero;
  if (valor === null) return "—";
  switch (unidade) {
    case "percentual":
      return `${umaCasa.format(valor)}%`;
    case "segundos":
      return duracaoLegivel(valor);
    case "minutos":
      return duracaoLegivel(valor * 60);
    case "nota":
      return umaCasa.format(valor);
    default:
      return inteiro.format(valor);
  }
}

/** O estado do chamado e o detalhe da linha, juntos: "Em atendimento · Fora do prazo". */
export function situacaoDoItem(item: Pick<ItemDoDrill, "estado" | "detalhe">): string {
  const estado = item.estado ? (ROTULO_DO_ESTADO[item.estado as EstadoDoTicket] ?? item.estado) : null;
  return [estado, item.detalhe].filter(Boolean).join(" · ") || "—";
}

/** Classes da largura de um widget na grade de 4 colunas (inteiras para o Tailwind achar). */
const LARGURA_NA_GRADE: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
};

export function larguraNaGrade(colunas: number): string {
  return LARGURA_NA_GRADE[colunas] ?? LARGURA_NA_GRADE[1]!;
}

/** A query string dos filtros de um widget, sem os vazios. */
export function consultaDoWidget(filtros: FiltrosGuardados, extras: Record<string, string> = {}) {
  const parametros = new URLSearchParams();
  if (filtros.periodo) parametros.set("periodo", filtros.periodo);
  if (filtros.organizacao_id) parametros.set("organizacao_id", filtros.organizacao_id);
  if (filtros.equipe_id) parametros.set("equipe_id", filtros.equipe_id);
  for (const [chave, valor] of Object.entries(extras)) parametros.set(chave, valor);
  return parametros.toString();
}

/* ---- o rascunho do editor ---- */

export interface WidgetEmEdicao {
  /** Chave local, só para a lista do React: o servidor regrava os widgets inteiros. */
  chave: string;
  tipo: string;
  colunas: number;
  filtros: FiltrosGuardados;
}

export interface RascunhoDePainel {
  nome: string;
  descricao: string;
  widgets: WidgetEmEdicao[];
  papeis: string[];
}

let contador = 0;
function chaveLocal(): string {
  contador += 1;
  return `w${contador}`;
}

export function rascunhoVazio(): RascunhoDePainel {
  return { nome: "", descricao: "", widgets: [], papeis: [] };
}

export function rascunhoDo(painel: PainelNoConsole): RascunhoDePainel {
  return {
    nome: painel.nome,
    descricao: painel.descricao,
    widgets: painel.widgets.map((w) => ({
      chave: chaveLocal(),
      tipo: w.tipo,
      colunas: w.colunas,
      filtros: w.filtros,
    })),
    papeis: painel.papeis,
  };
}

export function novoWidget(tipo: string): WidgetEmEdicao {
  return { chave: chaveLocal(), tipo, colunas: 1, filtros: {} };
}

/** Troca o widget de lugar com o vizinho (`-1` sobe, `1` desce); nas pontas não faz nada. */
export function moverWidget(
  widgets: readonly WidgetEmEdicao[],
  indice: number,
  sentido: -1 | 1,
): WidgetEmEdicao[] {
  const destino = indice + sentido;
  if (destino < 0 || destino >= widgets.length) return [...widgets];
  const copia = [...widgets];
  [copia[indice], copia[destino]] = [copia[destino]!, copia[indice]!];
  return copia;
}

export function validarRascunho(rascunho: RascunhoDePainel): string | null {
  if (!rascunho.nome.trim()) return "Dê um nome ao painel.";
  if (rascunho.widgets.length === 0) return "Escolha ao menos um widget para o painel.";
  return null;
}

/** O corpo que a API espera: a ordem da lista é a ordem da grade, e filtro vazio não vai. */
export function corpoDoPainel(rascunho: RascunhoDePainel) {
  return {
    nome: rascunho.nome.trim(),
    descricao: rascunho.descricao.trim(),
    widgets: rascunho.widgets.map((w) => ({
      tipo: w.tipo,
      colunas: w.colunas,
      filtros: Object.fromEntries(Object.entries(w.filtros).filter(([, valor]) => valor)),
    })),
    papeis: rascunho.papeis,
  };
}

/* ---------------------------------------------------------------------------------- consultas */

export function useCatalogoDosPaineis(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_PAINEIS, "catalogo"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/paineis/catalogo", esquemaCatalogo, { signal }),
  });
}

export function usePaineisDaEquipe(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_PAINEIS, "lista"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/paineis", z.array(esquemaPainelNoConsole), { signal }),
  });
}

export function useSalvarPainel() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rascunho }: { id?: string; rascunho: RascunhoDePainel }) =>
      id
        ? atualizarJson(`/paineis/${encodeURIComponent(id)}`, esquemaPainelNoConsole, corpoDoPainel(rascunho))
        : enviarJson("/paineis", esquemaPainelNoConsole, corpoDoPainel(rascunho)),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_PAINEIS }),
  });
}

export function useApagarPainel() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`/paineis/${encodeURIComponent(id)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_PAINEIS }),
  });
}

/** O painel inicial do console. Falha de rede não vira erro na tela: vale o console de sempre. */
export function usePainelInicial() {
  return useQuery({
    queryKey: [...CHAVE_DOS_PAINEIS, "inicial"],
    retry: false,
    queryFn: ({ signal }) => pedirJson("/paineis/inicial", esquemaPainelInicial, { signal }),
  });
}

export function usePainelParaVer(id: string) {
  return useQuery({
    queryKey: [...CHAVE_DOS_PAINEIS, "ver", id],
    queryFn: ({ signal }) =>
      pedirJson(`/paineis/${encodeURIComponent(id)}`, esquemaPainelParaVer, { signal }),
  });
}

export function useDadosDoWidget(tipo: string, filtros: FiltrosGuardados) {
  const consulta = consultaDoWidget(filtros);
  return useQuery({
    queryKey: [...CHAVE_DOS_PAINEIS, "widget", tipo, consulta],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      pedirJson(
        `/paineis/widgets/${encodeURIComponent(tipo)}${consulta ? `?${consulta}` : ""}`,
        esquemaDadosDoWidget,
        { signal },
      ),
  });
}

export function useDetalheDoNumero(
  tipo: string,
  numero: string | null,
  filtros: FiltrosGuardados,
) {
  const consulta = consultaDoWidget(filtros, { numero: numero ?? "" });
  return useQuery({
    queryKey: [...CHAVE_DOS_PAINEIS, "detalhe", tipo, consulta],
    enabled: numero !== null,
    queryFn: ({ signal }) =>
      pedirJson(
        `/paineis/widgets/${encodeURIComponent(tipo)}/detalhe?${consulta}`,
        esquemaDrill,
        { signal },
      ),
  });
}
